import { useState, useEffect } from "react";
import { Modal, Button, Input, Rate, message } from "antd";
import { DollarOutlined, RiseOutlined, CommentOutlined, AimOutlined, ShareAltOutlined } from "@ant-design/icons";
import { fireDB } from "../../funcs/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import getActiveUserId from "../../funcs/getActiveUserId";

const SURVEY_COLLECTION = "survey-insights-2026-jul";

const INSIGHT_OPTIONS = [
  { value: "sales_reports", label: "Reports that help to understand my sales more", icon: <DollarOutlined /> },
  { value: "followers_growth", label: "Reports to understand followers increasing", icon: <RiseOutlined /> },
  { value: "engagement_reports", label: "Reports to understand engagement", icon: <CommentOutlined /> },
  { value: "customer_intent", label: "Reports to understand customers/followers intent", icon: <AimOutlined /> },
  { value: "engagement_metrics_brand", label: "Engagement metrics to share to Brand Collabs", icon: <ShareAltOutlined /> },
];

function InsightsSurvey({ accountCreatedAt }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0); // 0: options, 1: free text, 2: rating, 3: issue (if low rating)
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [freeText, setFreeText] = useState("");
  const [rating, setRating] = useState(0);
  const [issueText, setIssueText] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    checkSurveyStatus();
  }, [accountCreatedAt]);

  const checkSurveyStatus = async () => {
    console.log("[InsightsSurvey] checking... accountCreatedAt:", accountCreatedAt, "userId:", getActiveUserId());
    if (!getActiveUserId()) { console.log("[InsightsSurvey] no userId"); return; }
    if (!accountCreatedAt) { console.log("[InsightsSurvey] no accountCreatedAt"); return; }

    // Parse account creation date (handles Python datetime format "2025-06-27 14:30:00.123456")
    const created = new Date(accountCreatedAt.replace(" ", "T"));
    const now = new Date();
    const daysSinceCreation = Math.floor((now - created) / (1000 * 60 * 60 * 24));
    console.log("[InsightsSurvey] account age days:", daysSinceCreation, "created:", created);
    if (isNaN(daysSinceCreation) || daysSinceCreation < 7) { console.log("[InsightsSurvey] account too new or invalid date"); return; }

    const docRef = doc(fireDB, SURVEY_COLLECTION, getActiveUserId());
    const docSnap = await getDoc(docRef);
    console.log("[InsightsSurvey] firebase doc exists:", docSnap.exists());

    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.completed) {
        console.log("[InsightsSurvey] already completed");
        setSubmitted(true);
        return;
      }
      console.log("[InsightsSurvey] opening modal");
      setOpen(true);
    } else {
      console.log("[InsightsSurvey] no doc, opening modal");
      setOpen(true);
    }
  };

  const handleSubmit = async () => {
    const docRef = doc(fireDB, SURVEY_COLLECTION, getActiveUserId());
    await setDoc(docRef, {
      completed: true,
      selected_options: selectedOptions,
      free_text: freeText,
      rating: rating,
      issue_text: issueText,
      submitted_at: new Date(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    setOpen(false);
    setSubmitted(true);
    message.success("Thanks for your feedback!");
  };

  const renderStep = () => {
    if (step === 0) {
      return (
        <div>
          <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>What kind of insights would help you the most?</p>
          <p style={{ color: "#888", marginBottom: 20, fontSize: 13 }}>Select up to 2 options</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {INSIGHT_OPTIONS.map((opt) => {
              const isSelected = selectedOptions.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  onClick={() => {
                    setSelectedOptions(prev => {
                      if (prev.includes(opt.value)) {
                        return prev.filter(v => v !== opt.value);
                      }
                      if (prev.length >= 2) {
                        message.warning("You can select up to 2 options");
                        return prev;
                      }
                      return [...prev, opt.value];
                    });
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    background: isSelected ? "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.08) 100%)" : "#fff",
                    border: `2px solid ${isSelected ? "#7c3aed" : "#e8ecff"}`,
                    borderRadius: 14,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    position: "relative",
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                    background: isSelected ? "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.2) 100%)" : "#f8f9ff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 18, color: isSelected ? "#7c3aed" : "#666",
                  }}>
                    {opt.icon}
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 500, color: "#333", flex: 1 }}>{opt.label}</span>
                  {isSelected && (
                    <div style={{
                      width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                      background: "#7c3aed",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>✓</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div>
          <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Share more details</p>
          <p style={{ color: "#888", marginBottom: 16, fontSize: 13 }}>Tell us more about what you selected — what specific data or reports would help your business?</p>
          <Input.TextArea
            rows={4}
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="E.g. I want to see which posts drive the most DMs, or a weekly report on follower growth..."
            style={{ borderRadius: 10, fontSize: 14, padding: 12 }}
          />
        </div>
      );
    }

    if (step === 2) {
      return (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>How would you rate InstantDM?</p>
          <p style={{ color: "#888", marginBottom: 24, fontSize: 13 }}>Your honest rating helps us improve</p>
          <Rate
            value={rating}
            onChange={setRating}
            style={{ fontSize: 40 }}
          />
        </div>
      );
    }

    if (step === 3) {
      return (
        <div>
          <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>We're sorry to hear that</p>
          <p style={{ color: "#888", marginBottom: 16, fontSize: 13 }}>What issues are you facing? We'd love to fix them.</p>
          <Input.TextArea
            rows={4}
            value={issueText}
            onChange={(e) => setIssueText(e.target.value)}
            placeholder="Describe what's not working or what you expected..."
            style={{ borderRadius: 10, fontSize: 14, padding: 12 }}
          />
        </div>
      );
    }
  };

  const handleNext = () => {
    if (step === 0) {
      if (selectedOptions.length === 0) {
        message.warning("Please select at least one option");
        return;
      }
      setStep(1);
    } else if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      if (rating === 0) {
        message.warning("Please give a rating");
        return;
      }
      if (rating <= 3) {
        setStep(3);
      } else {
        handleSubmit();
      }
    } else if (step === 3) {
      handleSubmit();
    }
  };

  return (
    <>
      {open && (
        <Modal
          open={open}
          footer={null}
          closable={false}
          maskClosable={false}
          centered
          width={520}
          title={null}
        >
          <div className="survey-form" style={{ padding: "8px 0" }}>
            {renderStep()}
            <div style={{ marginTop: 28, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
              {step > 0 && (
                <Button onClick={() => setStep(step - 1)}>Back</Button>
              )}
              <Button type="primary" onClick={handleNext} style={{ borderRadius: 6, padding: "4px 24px", height: 38 }}>
                {(step === 2 && rating > 3) || step === 3 ? "Submit" : "Next"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default InsightsSurvey;
